import { CSSProperties } from 'react'
import { omit } from 'util/object'

type CSSVariables = Partial<Record<`--${string}`, string>>

type OptionalNumber = number | string | null | undefined

export type Position = [OptionalNumber, OptionalNumber, OptionalNumber, OptionalNumber]

export function getLineHeight(line: number | undefined): string | number | undefined {
  if (line == null) return undefined
  if (line < 4) return line
  return line + 'px'
}

export function getPositionStyle(pin: Position | undefined): CSSProperties | undefined {
  if (pin == null) return undefined
  if (pin.length === 4) {
    const [top, right, bottom, left] = pin.map(n => n == null ? undefined : n)
    return omit({ position: 'absolute', top, right, bottom, left })
  }
  return undefined
}

export function getPinStyle(pin: Position | undefined): CSSProperties | undefined {
  const position = getPositionStyle(pin)
  if (pin?.length === 4) {
    const [top, right, bottom, left] = pin
    const h = left != null && right != null
    const v = top != null && bottom != null
    const margin = h && v ? 'auto' : h ? '0 auto' : v ? 'auto 0' : undefined
    const width = h ? 'max-content' : undefined
    const height = v ? 'max-content' : undefined
    return omit({ ...position, margin, width, height })
  }
  return position
}

export type Padding = number | string | [number | string, number | string] | [number | string, number | string, number | string, number | string]

export function getPaddingStyle(pad: Padding | undefined): CSSProperties | undefined {
  if (pad == null) return undefined
  if (typeof pad === 'number') {
    const padding = pad
    return { padding }
  }
  if (pad.length === 2) {
    const [paddingBlock, paddingInline] = pad
    return { paddingBlock, paddingInline }
  }
  if (pad.length === 4) {
    const [paddingTop, paddingRight, paddingBottom, paddingLeft] = pad
    return { paddingTop, paddingRight, paddingBottom, paddingLeft }
  }
  return undefined
}

export type Margin = number | [number, number] | [number | string, number | string, number | string, number | string]

export function getMarginStyle(margin: Margin | undefined): CSSProperties | undefined {
  if (margin == null) return undefined
  if (typeof margin === 'number') {
    return { margin }
  }
  if (margin.length === 2) {
    const [marginBlock, marginInline] = margin
    return { marginBlock, marginInline }
  }
  if (margin.length === 4) {
    const [marginTop, marginRight, marginBottom, marginLeft] = margin
    return { marginTop, marginRight, marginBottom, marginLeft }
  }
  return undefined
}

export type Gap = number | [number, number]

export function getGapStyle(gap: Gap | undefined): CSSProperties | undefined {
  if (gap == null) return undefined
  if (typeof gap === 'number') {
    return { gap }
  }
  if (gap.length === 2) {
    const [rowGap, columnGap] = gap
    return { rowGap, columnGap }
  }
  return undefined
}

export function styles(style: CSSProperties | CSSVariables | undefined): CSSProperties | undefined {
  if (!style) return undefined
  const fixed = omit<CSSProperties>(style ?? {})
  if (Object.entries(fixed ?? {}).length === 0) return undefined
  return style
}
